/**
 * What the row preview says about a file (16 idea 7), read in the browser from the file the preview already
 * downloads — no server work, no extra library (fflate is already a dependency for compressed PDFs):
 *   PDF   → pages and sheet size: "PDF · 2.4 MB · A1 · 1 sheet"
 *   image → pixel size:            "PNG · 340 KB · 1920 × 1080 px"
 *   CSV   → the first rows for a mini table: "CSV · 83 B · 12 rows"
 */
import { unzlibSync } from 'fflate';

/** ISO A sizes and the common US ones, portrait, in millimetres. */
const PAPER_SIZES = [
  ['A0', 841, 1189], ['A1', 594, 841], ['A2', 420, 594], ['A3', 297, 420], ['A4', 210, 297], ['A5', 148, 210],
  ['Letter', 216, 279], ['Legal', 216, 356], ['Tabloid', 279, 432],
];
const PT_TO_MM = 25.4 / 72;
const MAX_STREAMS = 60;
const MAX_TEXT_BYTES = 12 * 1024 * 1024;

/** 1684 × 2384 pt → { name: 'A1', widthMm: 594, heightMm: 841, landscape: false }; unknown sizes keep a null name. */
export function paperSize(widthPt, heightPt) {
  const w = Math.abs(Number(widthPt)) * PT_TO_MM;
  const h = Math.abs(Number(heightPt)) * PT_TO_MM;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  const short = Math.min(w, h);
  const long = Math.max(w, h);
  const hit = PAPER_SIZES.find(([, sw, sh]) => Math.abs(short - sw) / sw < 0.03 && Math.abs(long - sh) / sh < 0.03);
  return { name: hit ? hit[0] : null, widthMm: Math.round(w), heightMm: Math.round(h), landscape: w > h };
}

function latin1(bytes, limit = MAX_TEXT_BYTES) {
  const view = bytes.subarray(0, Math.min(bytes.length, limit));
  let text = '';
  for (let index = 0; index < view.length; index += 0x8000) {
    text += String.fromCharCode.apply(null, view.subarray(index, index + 0x8000));
  }
  return text;
}

/** The text of every compressed object stream (where newer PDFs keep their page objects). */
function objectStreams(text, bytes) {
  const found = [];
  const pattern = /\/Type\s*\/ObjStm\b/g;
  let match = pattern.exec(text);
  while (match && found.length < MAX_STREAMS) {
    const start = text.indexOf('stream', match.index);
    const end = start === -1 ? -1 : text.indexOf('endstream', start);
    if (start !== -1 && end !== -1) {
      let from = start + 'stream'.length;
      if (text[from] === '\r') from += 1;
      if (text[from] === '\n') from += 1;
      try {
        found.push(latin1(unzlibSync(bytes.subarray(from, end)), 4 * 1024 * 1024));
      } catch {
        // not Flate, or cut short — the plain text still counts
      }
    }
    match = pattern.exec(text);
  }
  return found;
}

const countPages = (text) => (text.match(/\/Type\s*\/Page(?![a-zA-Z])/g) || []).length;

function pageTreeCount(text) {
  let best = 0;
  const pattern = /\/Type\s*\/Pages\b/g;
  let match = pattern.exec(text);
  while (match) {
    const objStart = Math.max(text.lastIndexOf('obj', match.index), match.index - 600);
    const objEnd = text.indexOf('>>', match.index);
    const windowText = text.slice(Math.max(0, objStart), objEnd === -1 ? match.index + 600 : Math.max(objEnd, match.index + 200));
    const count = windowText.match(/\/Count\s+(\d+)/);
    if (count) best = Math.max(best, Number(count[1]));
    match = pattern.exec(text);
  }
  return best;
}

/** { pages, widthPt, heightPt, paper } from the bytes of a PDF; null when it is not one. */
export function readPdfInfo(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || []);
  if (bytes.length < 8) return null;
  const text = latin1(bytes);
  if (!text.startsWith('%PDF-') && !text.slice(0, 1024).includes('%PDF-')) return null;
  const sources = [text, ...objectStreams(text, bytes)];
  const counted = sources.reduce((sum, part) => sum + countPages(part), 0);
  const tree = sources.reduce((max, part) => Math.max(max, pageTreeCount(part)), 0);
  const pages = tree || counted || null;
  let widthPt = null;
  let heightPt = null;
  for (const part of sources) {
    const box = part.match(/\/MediaBox\s*\[\s*(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s*\]/);
    if (box) {
      widthPt = Math.abs(Number(box[3]) - Number(box[1]));
      heightPt = Math.abs(Number(box[4]) - Number(box[2]));
      break;
    }
  }
  return { pages, widthPt, heightPt, paper: widthPt && heightPt ? paperSize(widthPt, heightPt) : null };
}

/** A small, quote-aware CSV reader for the preview: the first `maxRows` rows, at most `maxCols` columns. */
export function readCsvPreview(text, { maxRows = 6, maxCols = 5 } = {}) {
  const source = String(text || '').replace(/^﻿/, '');
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  let total = 0;
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (quoted) {
      if (char === '"' && source[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"') quoted = true;
    else if (char === ',' || char === ';' || char === '\t') {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[index + 1] === '\n') index += 1;
      row.push(cell);
      cell = '';
      if (row.some((value) => value.trim())) {
        total += 1;
        if (rows.length < maxRows) rows.push(row.slice(0, maxCols));
      }
      row = [];
    } else cell += char;
  }
  row.push(cell);
  if (row.some((value) => value.trim())) {
    total += 1;
    if (rows.length < maxRows) rows.push(row.slice(0, maxCols));
  }
  return { rows, total };
}

/** 'pdf' | 'image' | 'csv' | 'other' from the stored file name. */
export function previewKind(doc) {
  const name = String(doc?.file_name || doc?.file_url || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return 'image';
  if (ext === 'csv') return 'csv';
  return 'other';
}

const SHEET_TYPES = new Set(['drawing', 'shop_drawing', 'schematic']);

/** "A1 · 1 sheet" (drawings) / "A4 · 12 pages" — the PDF part of the caption. */
export function pdfCaption(info, docType) {
  if (!info) return '';
  const noun = SHEET_TYPES.has(String(docType || '').toLowerCase()) ? 'sheet' : 'page';
  const size = info.paper ? (info.paper.name || `${info.paper.widthMm} × ${info.paper.heightMm} mm`) : '';
  const pages = info.pages ? `${info.pages} ${noun}${info.pages === 1 ? '' : 's'}` : '';
  return [size, pages].filter(Boolean).join(' · ');
}

/** blob.arrayBuffer() / blob.text(), with a FileReader fallback for older engines (and jsdom). */
function readBlob(blob, as) {
  if (as === 'text' && typeof blob?.text === 'function') return blob.text();
  if (as === 'arrayBuffer' && typeof blob?.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    if (as === 'text') reader.readAsText(blob);
    else reader.readAsArrayBuffer(blob);
  });
}

export const blobText = (blob) => readBlob(blob, 'text');
export const blobBytes = async (blob) => new Uint8Array(await readBlob(blob, 'arrayBuffer'));
